import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContextType, AuthUser } from '@/types';
import { API_BASE_URL, apiRequest, tokenStorage, apiAuth, apiClient } from '@/lib/api';
import { normalizeAuthUser, extractErrorMessage } from './auth-helpers';

// ============================================================================
// CONTEXT
// ============================================================================

// Exportado (e não só o `useAuth` que lança sem provider) para quem precisa
// de ler o utilizador actual sem exigir `AuthProvider` — ex.: um hook
// partilhado usado tanto dentro da app como em testes que montam a página
// sem essa árvore de providers.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Session expiry tracking (prevent duplicate notifications)
  const sessionExpiredShown = useRef(false);

  // Logout tracking (prevent double calls in React Strict Mode)
  const isLoggingOut = useRef(false);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Handle unauthorized responses (401)
   * Clears authentication state and redirects to login
   */
  const handleUnauthorized = useCallback(() => {
    // Clear authentication state
    tokenStorage.clear();
    setUser(null);

    // Show session expired notification (once per session)
    if (!sessionExpiredShown.current) {
      sessionExpiredShown.current = true;
      console.warn('Session expired. Please login again.');

      // Reset flag after navigation to allow future notifications
      setTimeout(() => {
        sessionExpiredShown.current = false;
      }, 1000);
    }

    // Redirect to login with return path
    // Skip redirect if already on a public auth route (login, forgot/reset password)
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/candidaturas'];
    const currentPath = location.pathname + location.search;
    if (!publicPaths.some((p) => location.pathname.startsWith(p))) {
      navigate('/login', {
        replace: true,
        state: { from: currentPath, sessionExpired: true },
      });
    }
  }, [navigate, location]);

  /**
   * Fetch current user from /auth/me endpoint.
   * Validates and normalizes the response into AuthUser.
   */
  const fetchMe = useCallback(async (): Promise<void> => {
    try {
      const response = await apiRequest('/auth/me');
      const validatedUser = normalizeAuthUser(response);
      setUser(validatedUser);
    } catch (error) {
      // Log error for debugging
      console.error('Failed to fetch user:', error);

      // Clear authentication state
      tokenStorage.clear();
      setUser(null);

      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login with email and password
   * Uses OAuth2 form-urlencoded format
   *
   * Endpoint: POST /auth/login
   * Sets HttpOnly cookie with JWT token
   */
  const login = useCallback(
    async (email: string, password: string, rememberMe = false): Promise<void> => {
      // Input validation
      if (!email || !password) {
        throw new Error('E-mail e palavra-passe são obrigatórios');
      }

      if (!email.includes('@')) {
        throw new Error('Insere um endereço de e-mail válido');
      }

      // Prepare OAuth2 form data
      const form = new URLSearchParams();
      form.append('username', email.trim());
      form.append('password', password);
      form.append('remember_me', String(rememberMe));

      try {
        // Send login request with credentials
        await axios.post(`${API_BASE_URL}/auth/login`, form, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true, // Include cookies for HttpOnly authentication
        });

        // Fetch user profile after successful authentication
        await fetchMe();
      } catch (error) {
        // Extract and throw meaningful error message
        const message = extractErrorMessage(
          error,
          'Falha na autenticação. Verifica as tuas credenciais e tenta novamente.'
        );
        throw new Error(message);
      }
    },
    [fetchMe]
  );

  /**
   * Logout user and clear session
   * Calls backend to invalidate HttpOnly cookie
   *
   * Endpoint: POST /auth/logout
   * Clears HttpOnly cookie
   *
   * Protected against double calls (React Strict Mode)
   */
  const logout = useCallback(async (): Promise<void> => {
    // Prevent double logout in React Strict Mode
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    try {
      // Call backend logout endpoint to clear session
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Log error but continue with client-side logout
      console.error('Backend logout failed:', error);
    } finally {
      // Always clear local authentication state
      tokenStorage.clear();
      setUser(null);
      isLoggingOut.current = false;

      // Navigate to login page
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Register unauthorized handler
   * Enables automatic session expiry handling
   */
  useEffect(() => {
    apiAuth.setUnauthorizedHandler(handleUnauthorized);

    return () => {
      apiAuth.clearUnauthorizedHandler();
    };
  }, [handleUnauthorized]);

  /**
   * Initialize authentication on mount
   * Attempts to restore session from HttpOnly cookie
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await fetchMe();
      } catch (_error) {
        // No valid session - expected for logged-out users
        // Error already handled in fetchMe
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [fetchMe]);

  /**
   * Renovação silenciosa da sessão enquanto a pessoa está a trabalhar.
   *
   * O access token dura 30 minutos; renova-se a cada 5, bastante antes de
   * expirar. A margem existe para o portátil que adormece ou a rede que falha:
   * há várias tentativas dentro da validade de um token.
   *
   * Isto é a rede de segurança, não o mecanismo principal — mesmo que todas
   * estas renovações falhem, o interceptor em `lib/api.ts` renova no próximo
   * 401 e repete o pedido. Só quando ESSA renovação falha é que há logout.
   *
   * Também renova ao voltar ao separador, para quem regressa de uma pausa longa
   * não esperar pelo tique seguinte.
   */
  useEffect(() => {
    if (!user) return;

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

    const silentRefresh = async () => {
      try {
        await apiClient.post('/auth/refresh');
      } catch (_error) {
        // 401 already handled by interceptor; other errors are transient.
      }
    };

    const interval = window.setInterval(silentRefresh, REFRESH_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        silentRefresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // ============================================================================
  // CONTEXT VALUE (Memoized for Performance)
  // ============================================================================

  /**
   * Memoized context value prevents unnecessary re-renders
   * Only updates when dependencies change
   */
  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      fetchMe,
    }),
    [user, isLoading, login, logout, fetchMe]
  );

  return <AuthContext value={contextValue}>{children}</AuthContext>;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useAuth Hook
 * Provides access to authentication context
 *
 * @throws {Error} If used outside AuthProvider
 * @returns {AuthContextType} Authentication context
 *
 * @example
 * const { user, isAuthenticated, login, logout } = useAuth();
 *
 * if (isAuthenticated) {
 *   console.log('Logged in as:', user.name);
 * }
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
